/**
 * Framework schemas for the blueprint wizard.
 *
 * Mirrors the per-framework backends in
 * core/src/app/models/frameworks/{tensorflow,pytorch,sklearn,xgboost}/
 * so the wizard knows what fields to show, what the defaults are,
 * and what validation rules to apply BEFORE submitting to the API.
 */

// Aliases the FE picker exposes -> canonical lowercase the BE expects.
// Mirrors _normalize_framework() in core/src/api/blueprint/management.py.
export const FRAMEWORK_DISPLAY_TO_CANONICAL = {
  TensorFlow: 'tensorflow',
  PyTorch: 'pytorch',
  Keras: 'tensorflow',
  'Scikit-learn': 'sklearn',
  XGBoost: 'xgboost',
};

export const SUPPORTED_FRAMEWORKS = ['TensorFlow', 'PyTorch', 'Scikit-learn', 'XGBoost'];

/**
 * Returns the wizard "kind" for a framework:
 *   - 'neural_network' for TF / PyTorch (use Model Builder layer editor)
 *   - 'estimator' for sklearn / XGBoost (use Estimator + Hyperparameters editor)
 */
export const wizardKind = (display) => {
  const canonical = FRAMEWORK_DISPLAY_TO_CANONICAL[display] || 'tensorflow';
  if (canonical === 'tensorflow' || canonical === 'pytorch') return 'neural_network';
  if (canonical === 'sklearn' || canonical === 'xgboost') return 'estimator';
  return 'neural_network';
};

/**
 * sklearn estimators surfaced in the wizard. Mirrors
 * SUPPORTED_ESTIMATORS in
 * core/src/app/models/frameworks/sklearn/builder.py.
 */
export const SKLEARN_ESTIMATORS = [
  {
    name: 'RandomForestRegressor',
    description: 'Bagged decision trees. Strong default for tabular regression.',
    hyperparameters: [
      { key: 'n_estimators',     label: 'Trees',        type: 'int',   default: 100, min: 1,    max: 2000 },
      { key: 'max_depth',        label: 'Max depth',    type: 'int',   default: 10,  min: 1,    max: 50,  optional: true },
      { key: 'min_samples_split',label: 'Min samples split', type: 'int', default: 2, min: 2,  max: 20 },
      { key: 'random_state',     label: 'Random state', type: 'int',   default: 42 },
    ],
  },
  {
    name: 'GradientBoostingRegressor',
    description: 'Boosted decision trees. Higher accuracy, slower to fit.',
    hyperparameters: [
      { key: 'n_estimators',  label: 'Trees',         type: 'int',   default: 100, min: 1,   max: 2000 },
      { key: 'max_depth',     label: 'Max depth',     type: 'int',   default: 3,   min: 1,   max: 20 },
      { key: 'learning_rate', label: 'Learning rate', type: 'float', default: 0.1, min: 0.001, max: 1.0, step: 0.01 },
      { key: 'subsample',     label: 'Subsample',     type: 'float', default: 1.0, min: 0.1, max: 1.0, step: 0.05 },
    ],
  },
  {
    name: 'ExtraTreesRegressor',
    description: 'Extra-randomized trees. Faster than RandomForest, similar accuracy.',
    hyperparameters: [
      { key: 'n_estimators', label: 'Trees',     type: 'int', default: 100, min: 1, max: 2000 },
      { key: 'max_depth',    label: 'Max depth', type: 'int', default: 10,  min: 1, max: 50, optional: true },
    ],
  },
  {
    name: 'LinearRegression',
    description: 'Ordinary least squares. Fast baseline, no hyperparameters.',
    hyperparameters: [],
  },
  {
    name: 'Ridge',
    description: 'L2-regularized linear regression. Use when features are correlated.',
    hyperparameters: [
      { key: 'alpha', label: 'Regularization (alpha)', type: 'float', default: 1.0, min: 0.0, max: 100.0, step: 0.1 },
    ],
  },
  {
    name: 'Lasso',
    description: 'L1-regularized linear regression. Performs feature selection.',
    hyperparameters: [
      { key: 'alpha', label: 'Regularization (alpha)', type: 'float', default: 1.0, min: 0.0, max: 100.0, step: 0.1 },
    ],
  },
  {
    name: 'ElasticNet',
    description: 'Combines L1 + L2 regularization.',
    hyperparameters: [
      { key: 'alpha',    label: 'Regularization (alpha)', type: 'float', default: 1.0, min: 0.0, max: 100.0, step: 0.1 },
      { key: 'l1_ratio', label: 'L1 ratio',               type: 'float', default: 0.5, min: 0.0, max: 1.0,   step: 0.05 },
    ],
  },
  {
    name: 'MLPRegressor',
    description: 'Small neural network (fully connected). Good for non-linear problems without writing a layer stack.',
    hyperparameters: [
      { key: 'hidden_layer_sizes', label: 'Hidden layer size', type: 'int',   default: 100, min: 1, max: 1000 },
      { key: 'activation',         label: 'Activation',        type: 'select',default: 'relu', options: ['relu','tanh','logistic','identity'] },
      { key: 'max_iter',           label: 'Max iterations',    type: 'int',   default: 200, min: 10, max: 5000 },
      { key: 'learning_rate_init', label: 'Learning rate',     type: 'float', default: 0.001, min: 1e-5, max: 1.0, step: 0.0001 },
    ],
  },
  {
    name: 'KNeighborsRegressor',
    description: 'k-Nearest Neighbors. Strong for low-dimensional smooth functions.',
    hyperparameters: [
      { key: 'n_neighbors', label: 'Neighbors',        type: 'int',    default: 5,         min: 1, max: 100 },
      { key: 'weights',     label: 'Weighting',        type: 'select', default: 'uniform', options: ['uniform','distance'] },
    ],
  },
  {
    name: 'DecisionTreeRegressor',
    description: 'Single decision tree. Interpretable but easily overfits.',
    hyperparameters: [
      { key: 'max_depth',         label: 'Max depth',        type: 'int', default: 10, min: 1, max: 50, optional: true },
      { key: 'min_samples_split', label: 'Min samples split',type: 'int', default: 2,  min: 2, max: 20 },
    ],
  },
  {
    name: 'SVR',
    description: 'Support Vector Regression. Slow to fit on >10k samples.',
    hyperparameters: [
      { key: 'kernel',  label: 'Kernel',  type: 'select', default: 'rbf', options: ['linear','poly','rbf','sigmoid'] },
      { key: 'C',       label: 'C',       type: 'float',  default: 1.0,   min: 0.001, max: 1000.0, step: 0.1 },
      { key: 'epsilon', label: 'Epsilon', type: 'float',  default: 0.1,   min: 0.0,   max: 1.0,    step: 0.01 },
    ],
  },
];

/**
 * XGBoost hyperparameters surfaced in the wizard. Mirrors
 * _DEFAULTS in core/src/app/models/frameworks/xgboost/builder.py.
 */
export const XGBOOST_HYPERPARAMETERS = [
  { key: 'n_estimators',     label: 'Trees',           type: 'int',   default: 200,   min: 10,    max: 5000 },
  { key: 'max_depth',        label: 'Max depth',       type: 'int',   default: 6,     min: 1,     max: 20 },
  { key: 'learning_rate',    label: 'Learning rate',   type: 'float', default: 0.05,  min: 0.001, max: 1.0,  step: 0.01 },
  { key: 'subsample',        label: 'Subsample',       type: 'float', default: 1.0,   min: 0.1,   max: 1.0,  step: 0.05 },
  { key: 'colsample_bytree', label: 'Colsample bytree',type: 'float', default: 1.0,   min: 0.1,   max: 1.0,  step: 0.05 },
  { key: 'reg_alpha',        label: 'L1 reg (alpha)',  type: 'float', default: 0.0,   min: 0.0,   max: 100.0,step: 0.1 },
  { key: 'reg_lambda',       label: 'L2 reg (lambda)', type: 'float', default: 1.0,   min: 0.0,   max: 100.0,step: 0.1 },
  { key: 'objective',        label: 'Objective',       type: 'select',default: 'reg:squarederror', options: [
    'reg:squarederror','reg:squaredlogerror','reg:absoluteerror','reg:pseudohubererror',
  ]},
  { key: 'tree_method',      label: 'Tree method',     type: 'select',default: 'hist', options: ['auto','exact','approx','hist'] },
];

/**
 * Default hyperparameter values for a given estimator schema.
 */
export const defaultHyperparameters = (params) => {
  const out = {};
  (params || []).forEach((p) => {
    if (p.optional && (p.default === undefined || p.default === null)) return;
    out[p.key] = p.default;
  });
  return out;
};

/**
 * Coerces a hyperparameter input value to the right type. Used by
 * the wizard's onChange handlers so the saved blueprint never sends
 * stringly-typed numbers to the BE validator (which would reject
 * them as "not a number").
 */
export const coerceHyperparam = (param, raw) => {
  if (param.type === 'int') {
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : param.default;
  }
  if (param.type === 'float') {
    const n = parseFloat(raw);
    return Number.isFinite(n) ? n : param.default;
  }
  return raw;
};
